import { useCallback, useState } from 'react';
import { Text, Divider, Blockquote, Anchor, Badge, Group, Box } from '@mantine/core';
import { EditableText } from '@ayocore/platform/client/components/EditableText';
import { ViewPanel, InfoRow } from '@ayocore/platform/client/components/EntityViewLayout';
import {
  ReferenceSinglePicker,
  ReferenceMultiPicker,
  type ReferenceOption,
} from '@ayocore/platform/client/components/InlinePicker';
import { RepositoryCombobox } from '@ayocore/platform/client/components/RepositoryCombobox';
import { UserPicker } from '@ayocore/platform/identity/client/components/UserPicker';
import {
  useSegmentRepository,
  useTagRepository,
  useSalesAreaRepository,
  usePartnerRoleRepository,
} from '@ayocore/erp/crm/client/rpc';
import type { PartnerExtended } from '../PartnerViewController';
import type { Segment } from '@ayocore/erp/crm/schemas/segment.schema';
import type { Tag } from '@ayocore/erp/crm/schemas/tag.schema';

interface PartnerInfoProps {
  partner: PartnerExtended;
  segments?: Segment[];
  tags?: Tag[];
  canEdit?: boolean;
  /** Разрешена ли смена менеджера (клиент + роль CRM_OWNER/CRM_BRANCH_MANAGER) — считается в PartnerView. */
  canEditManager?: boolean;
  onNotesSave?: (value: string) => void | Promise<void>;
  onAddressSave?: (value: string) => void | Promise<void>;
  onSetSalesArea?: (salesAreaId: number | null) => void | Promise<void>;
  onSetSegments?: (segmentIds: number[]) => void | Promise<void>;
  onSetTags?: (tagIds: number[]) => void | Promise<void>;
  onSetRoles?: (roleIds: number[]) => void | Promise<void>;
  onManagerSave?: (managerId: number | null) => void | Promise<void>;
}

const placeholderClass = 'entity-view-placeholder-dimmed';

export function PartnerInfo({
  partner,
  segments = [],
  tags = [],
  canEdit = false,
  canEditManager = false,
  onNotesSave,
  onAddressSave,
  onSetSalesArea,
  onSetSegments,
  onSetTags,
  onSetRoles,
  onManagerSave,
}: PartnerInfoProps) {
  const segmentRepository = useSegmentRepository();
  const tagRepository = useTagRepository();
  const salesAreaRepository = useSalesAreaRepository();
  const partnerRoleRepository = usePartnerRoleRepository();
  const [rolesEditing, setRolesEditing] = useState(false);

  const fetchSegments = useCallback(async (): Promise<ReferenceOption[]> => {
    const rows = (await segmentRepository
      .where('is_active', true)
      .orderBy('name', 'asc')
      .limit(500)
      .fetch()) as Segment[];
    return (rows ?? []).map(s => ({
      id: s.id as number,
      label: s.name ?? `Segment #${s.id}`,
      hint: s.description ?? undefined,
    }));
  }, [segmentRepository]);

  const fetchSalesAreas = useCallback(async (): Promise<ReferenceOption[]> => {
    const rows = (await salesAreaRepository
      .filterActive()
      .orderBy('name', 'asc')
      .limit(500)
      .fetch()) as Array<{ id: number; name?: string | null; description?: string | null }>;
    return (rows ?? []).map(s => ({
      id: s.id,
      label: s.name ?? `Region #${s.id}`,
      hint: s.description ?? undefined,
    }));
  }, [salesAreaRepository]);

  const fetchTags = useCallback(async (): Promise<ReferenceOption[]> => {
    const rows = (await tagRepository
      .where('is_active', true)
      .orderBy('name', 'asc')
      .limit(500)
      .fetch()) as Tag[];
    return (rows ?? []).map(t => ({
      id: t.id as number,
      label: t.name ?? `Tag #${t.id}`,
      hint: t.description ?? undefined,
    }));
  }, [tagRepository]);

  const managerEditable = canEdit && canEditManager && !!onManagerSave;
  const segmentEditable = canEdit && !!onSetSegments;
  const tagEditable = canEdit && !!onSetTags;
  const roleEditable = canEdit && !!onSetRoles;
  const salesAreaEditable = canEdit && !!onSetSalesArea;

  const segmentIds = segments.map(s => s.id as number);
  const segmentLabels = segments.map(s => ({ id: s.id as number, label: s.name ?? '' }));
  const tagIds = tags.map(t => t.id as number);
  const tagLabels = tags.map(t => ({ id: t.id as number, label: t.name ?? '' }));
  const partnerRoles = partner.roles ?? [];
  const roleIds = partnerRoles.map(r => String(r.id));

  return (
    <ViewPanel>
      <InfoRow>
        <InfoRow.TitleCol>Менеджер</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <UserPicker
            value={partner.manager_id ?? null}
            user={partner.manager ?? null}
            onChange={id => void onManagerSave?.(id)}
            canEdit={managerEditable}
            placeholder='Не назначен'
          />
        </InfoRow.ValueCol>
      </InfoRow>
      <InfoRow>
        <InfoRow.TitleCol>Регион продаж</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <ReferenceSinglePicker
            value={partner.sales_area_id ?? null}
            selectedLabel={partner.sales_area?.name ?? null}
            onChange={id => void onSetSalesArea?.(id)}
            fetchOptions={fetchSalesAreas}
            canEdit={salesAreaEditable}
            placeholder='Не указан'
            emptyLabel='Не указан'
          />
        </InfoRow.ValueCol>
      </InfoRow>
      <InfoRow>
        <InfoRow.TitleCol>Сегмент</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <ReferenceMultiPicker
            value={segmentIds}
            selectedLabels={segmentLabels}
            onChange={ids => void onSetSegments?.(ids)}
            fetchOptions={fetchSegments}
            canEdit={segmentEditable}
            placeholder='Не указан'
            badgeColor='violet'
          />
        </InfoRow.ValueCol>
      </InfoRow>
      <InfoRow>
        <InfoRow.TitleCol>Теги</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <ReferenceMultiPicker
            value={tagIds}
            selectedLabels={tagLabels}
            onChange={ids => void onSetTags?.(ids)}
            fetchOptions={fetchTags}
            canEdit={tagEditable}
            placeholder='Не указаны'
            badgeColor='green'
          />
        </InfoRow.ValueCol>
      </InfoRow>
      <InfoRow>
        <InfoRow.TitleCol>Тип партнёра</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          {/* Резервируем высоту строки под инпут комбобокса, чтобы при переходе
              просмотр→редактирование строка не подпрыгивала. */}
          <Box style={{ display: 'flex', alignItems: 'center', minHeight: 30 }}>
            {rolesEditing ? (
              <RepositoryCombobox
                mode='multi'
                repo={partnerRoleRepository.where('is_active', true).orderBy('name', 'asc')}
                displayField='name'
                value={roleIds}
                onChange={ids => void onSetRoles?.(ids.map(Number))}
                placeholder='Не указан'
                dropdownWidth='max-content'
                width='100%'
                autoOpen
                onDropdownClose={() => setRolesEditing(false)}
              />
            ) : partnerRoles.length > 0 ? (
              <Group gap={4} wrap='wrap'>
                {partnerRoles.map(r => (
                  <Anchor
                    key={r.id}
                    component='button'
                    type='button'
                    underline='never'
                    onClick={() => roleEditable && setRolesEditing(true)}
                    style={{ cursor: roleEditable ? 'pointer' : 'default' }}
                  >
                    <Badge color='blue' variant='light' size='sm' style={{ cursor: 'inherit' }}>
                      {r.name}
                    </Badge>
                  </Anchor>
                ))}
              </Group>
            ) : roleEditable ? (
              <Anchor
                component='button'
                type='button'
                size='sm'
                onClick={() => setRolesEditing(true)}
              >
                Выбрать
              </Anchor>
            ) : (
              <Text size='sm' c='dimmed'>
                Не указан
              </Text>
            )}
          </Box>
        </InfoRow.ValueCol>
      </InfoRow>
      <InfoRow>
        <InfoRow.TitleCol>Адрес</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          {canEdit && onAddressSave ? (
            <EditableText
              value={partner.address ?? ''}
              size='sm'
              canEdit
              placeholder='Добавить адрес...'
              className={!partner.address ? placeholderClass : undefined}
              allowEmptySave
              onSave={v => void onAddressSave?.(v)}
              style={{
                minWidth: 0,
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            />
          ) : (
            <Text
              size='sm'
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {partner.address || 'Не указан'}
            </Text>
          )}
        </InfoRow.ValueCol>
      </InfoRow>

      <Divider my='md' />
      <Blockquote color='gray' radius='xs' p='xs' style={{ fontSize: 12 }}>
        <EditableText
          value={partner.notes ?? ''}
          canEdit={canEdit && !!onNotesSave}
          onSave={onNotesSave ?? (() => {})}
          placeholder='Добавить примечание...'
          size='sm'
          maxRows={6}
          style={{
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            display: 'block',
            verticalAlign: 'top',
            fontSize: 12,
          }}
        />
      </Blockquote>
    </ViewPanel>
  );
}
